import React from 'react';
import { useField, useForm } from 'react-final-form';
import { styled } from '@linaria/react';
import pluralize from 'pluralize';

import { useSettingsStore } from '@blocks/stores/settings';

import { ERROR_PAGES_LESS, ERROR_WORDS_MORE, WORDS_MAX, WORDS_MIN } from '../../constants';
import { FieldError, Input } from '../../ui';
import { Minus, Plus } from '../../ui/icon';
import { composeValidators, convertWordsToPages } from '../../utils';
import { lessThan, moreThan, required } from '../../utils/validators';

import PaperSizeHintButton from './paper-size-hint-button';

const isRequired = required(ERROR_WORDS_MORE);
const isLessThan = moreThan(ERROR_WORDS_MORE, WORDS_MIN);
const isMoreThan = lessThan(ERROR_PAGES_LESS, WORDS_MAX);

const WordsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: stretch;
`;

const WordsField = styled.div`
  position: relative;
  button:nth-of-type(2) {
    left: auto;
    right: var(--bb-field-border-width);
    border-radius: 0 var(--bb-field-border-radius) var(--bb-field-border-radius) 0;
  }

  input {
    text-align: center;
    padding-left: 44px;
    padding-right: 44px;
    box-sizing: border-box;
  }
`;

const WordsButton = styled.button`
  width: 43px;
  height: 43px;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  margin: 0;
  padding: 0;
  position: absolute;
  left: var(--bb-field-border-width);
  top: 50%;
  border-radius: var(--bb-field-border-radius) 0 0 var(--bb-field-border-radius);
  outline: none;
  margin-top: -21.5px;

  &:hover {
    cursor: pointer;
    background-color: var(--bb-field-button-background--hover);
  }
`;

const countPages = (value, meta, words_per_page = 275) =>
  meta.valid
    ? isNaN(parseInt(value, 10))
      ? `1 page`
      : `~ ${convertWordsToPages(value, words_per_page)} ${pluralize(
          'page',
          convertWordsToPages(value, words_per_page),
        )}`
    : undefined;

export default ({ domain }) => {
  const { mutators } = useForm();

  const { settings } = useSettingsStore(domain);
  const { words_per_page } = settings || {};
  const { meta, input } = useField('words_qty', {
    validate: composeValidators(isRequired, isLessThan, isMoreThan),
    type: 'number',
  });

  return (
    <>
      <WordsRow>
        <WordsField>
          <WordsButton
            type="button"
            onClick={mutators.decWords}
            disabled={meta.submitting}
            tabIndex="-1"
            aria-label="Minus"
          >
            <Minus />
          </WordsButton>

          <Input
            {...input}
            meta={meta}
            style={{
              appearance: 'textfield',
            }}
          />

          <WordsButton
            type="button"
            onClick={mutators.incWords}
            disabled={meta.submitting}
            tabIndex="-1"
            aria-label="Plus"
          >
            <Plus />
          </WordsButton>
        </WordsField>

        <PaperSizeHintButton>{countPages(input.value, meta, words_per_page)}</PaperSizeHintButton>
      </WordsRow>

      <FieldError meta={meta} />
    </>
  );
};
